import { Inertia } from '@inertiajs/inertia'
import { FunctionalComponent, h } from '@stencil/core'
import { JSXBase } from '@stencil/core/internal'

import { IconName } from '../icons'
import { classes } from '../utils/css'
import { addTimezoneParameterTo } from '../utils/url'
import googleLogo from './logos/google.png'
import microsoftLogo from './logos/microsoft.png'

export const AuthButton: FunctionalComponent<{
  image?: string
  icon?: IconName
  label: string
  available: boolean
  oopsError?: boolean
  missingScopes?: boolean
  unavailableCalendar?: boolean
  calendarNotFound?: boolean
  onClick?: (event: MouseEvent) => void
}> = (props) => {
  return (
    <div class='flex flex-col'>
      <button
        type='submit'
        disabled={!props.available}
        class={`relative flex items-center justify-between w-full h-11 max-h-11 flex-shrink-0 p-2 text-lg font-semibold leading-none truncate group flex-grow-1 focus:shadow-outline transition duration-200 rounded-lg bg-white text-surface-base hover:bg-gray-20
        ${classes(!props.available, 'pointer-events-none opacity-50 outline-none')}
      `}
        onClick={props.onClick}
      >
        <span
          class={`
          flex items-center
        `}
        >
          <span class='p-8px mr-8px rounded'>
            {props.icon && <ui-icon icon={props.icon} />}
            {props.image && <img src={props.image} class='h-18px' />}
          </span>
          <span>{props.label}</span>
        </span>

        {!props.available && (
          <span class='p-6px text-micro font-bold tracking-wide text-glyph-lighter uppercase rounded-md border border-glyph-light'>
            Coming Soon
          </span>
        )}
      </button>
      {props.missingScopes && <ErrorPanel message='All requested permissions are required.' />}
      {props.oopsError && <ErrorPanel message='Sorry, we were unable to authenticate you. Please try again.' />}
      {props.unavailableCalendar && <ErrorPanel message='Calendar connected by another user.' />}
      {props.calendarNotFound && <ErrorPanel message='Calendar not found.' />}
    </div>
  )
}

export const ErrorPanel: FunctionalComponent<{
  message: string
}> = (props) => {
  return (
    <div class='flex p-3 text-sm font-bold text-info-warn bg-info-warn bg-opacity-10 rounded-md spacing-x-3 spacing-y-1px'>
      {props.message}
    </div>
  )
}

export const GoogleAuthButton: FunctionalComponent<
  {
    authUrl: string
    label: string
    fromGoogle?: boolean
    showCalendarNotFoundError?: boolean
    showMissingScopesError?: boolean
    showOopsError?: boolean
    showUnavailableCalendarError?: boolean
  } & JSXBase.IntrinsicElements['form']
> = ({
  authUrl,
  label,
  fromGoogle = false,
  showCalendarNotFoundError = false,
  showMissingScopesError = false,
  showOopsError = false,
  showUnavailableCalendarError = false,
  ...attrs
}) => {
  return (
    // Add with Fragment to restore font
    // <link href='https://fonts.googleapis.com/css2?family=Roboto:wght@500&display=swap' rel='stylesheet'></link>

    <form
      id='google_form'
      method='post'
      action={addTimezoneParameterTo(authUrl)}
      //style={{ fontFamily: 'Roboto, sans-serif', fontWeight: '700' }}
      {...attrs}
    >
      <csrf-token />
      <AuthButton
        image={googleLogo}
        label={label}
        available
        missingScopes={fromGoogle && showMissingScopesError}
        oopsError={fromGoogle && showOopsError}
        unavailableCalendar={fromGoogle && showUnavailableCalendarError}
        calendarNotFound={fromGoogle && showCalendarNotFoundError}
      />
    </form>
  )
}

export const MicrosoftAuthButton: FunctionalComponent<
  {
    authUrl: string
    label: string
    fromMicrosoft?: boolean
    showCalendarNotFoundError?: boolean
    showMissingScopesError?: boolean
    showOopsError?: boolean
    showUnavailableCalendarError?: boolean
  } & JSXBase.IntrinsicElements['form']
> = ({
  authUrl,
  label,
  fromMicrosoft = false,
  showCalendarNotFoundError = false,
  showMissingScopesError = false,
  showOopsError = false,
  showUnavailableCalendarError = false,
  ...attrs
}) => {
  return (
    <form
      id='microsoft_form'
      method='post'
      action={addTimezoneParameterTo(authUrl)}
      // style={{ fontFamily: 'Segoe, sans-serif', fontWeight: '600', fontSize: '15px' }}
      {...attrs}
    >
      <csrf-token />
      <AuthButton
        image={microsoftLogo}
        label={label}
        available
        missingScopes={fromMicrosoft && showMissingScopesError}
        oopsError={fromMicrosoft && showOopsError}
        unavailableCalendar={fromMicrosoft && showUnavailableCalendarError}
        calendarNotFound={fromMicrosoft && showCalendarNotFoundError}
      />
    </form>
  )
}

export const SSOAuthButton: FunctionalComponent<{
  url: string
  label: string
}> = ({ url, label, ...attrs }) => {
  return <AuthButton icon='person-circle' label={label} available onClick={() => Inertia.visit(url)} {...attrs} />
}

export const ZoomSignInButton: FunctionalComponent<
  {
    signInUrl: string
  } & JSXBase.IntrinsicElements['form']
> = ({ signInUrl, ...attrs }) => {
  return (
    <form
      method='post'
      class='flex flex-col self-start mb-4 rounded-sm bg-fill-2'
      action={addTimezoneParameterTo(signInUrl)}
      {...attrs}
    >
      <csrf-token />
      <button
        type='submit'
        color='link'
        class='flex relative flex-shrink-0 justify-center items-center p-3 w-full text-xl font-semibold tracking-wide leading-none text-link hover:text-surface-lower truncate hover:bg-link rounded-md border border-link focus:shadow-outline transition duration-200 flex-grow-1'
        style={{ outline: 'none' }}
      >
        <ui-icon icon='camera-video' size='4xl' class='pr-3' />
        Sign in with Zoom
        <ui-icon icon='chevron-right' size='xl' class='pl-16' />
      </button>
    </form>
  )
}
